import { listA2UISurfaces, useA2UIClient } from 'lib/actions/a2ui'
import { Status } from 'lib/rpc'
import { getA2UIService, onA2UIServiceReady, type A2UIService, type OnA2UISurfaceCallback } from 'lib/ws/services/a2ui-service'
import type { A2UIEnvelope } from 'pbts/c1/api/a2ui/v1/a2ui'
import type { A2UIComponent } from 'pbts/c1/api/a2ui/v1/components'
import type { A2UISurface } from 'pbts/c1/api/a2ui/v1/surface'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { setByPointer } from './resolver'

export interface SurfaceState {
    surface: A2UISurface
    dataModel: Record<string, unknown>
    componentsMap: Map<string, A2UIComponent>
}

interface A2UIState {
    surfaces: Map<string, SurfaceState>
    status: 'idle' | 'connecting' | 'connected' | 'error'
    error?: string
}

type A2UIAction =
    | { type: 'SURFACE_CREATED'; surface: A2UISurface }
    | { type: 'SURFACE_UPDATED'; surface: A2UISurface }
    | { type: 'SURFACE_DELETED'; surfaceId: string }
    | { type: 'DATA_MODEL_UPDATED'; surfaceId: string; path: string; value: unknown }
    | { type: 'SET_STATUS'; status: A2UIState['status']; error?: string }
    | { type: 'SET_CONVERSATION_ID'; conversationId: string }
    | { type: 'PROCESS_ENVELOPE'; envelope: A2UIEnvelope }

/**
 * Parses an A2UISurface into SurfaceState by extracting the data model JSON
 * and building a lookup map for components by ID.
 */
function parseSurface(surface: A2UISurface): SurfaceState {
    let dataModel: Record<string, unknown> = {}
    if (surface.dataModelJson) {
        try {
            dataModel = JSON.parse(surface.dataModelJson)
        } catch {
            // Ignore parse errors
        }
    }
    const componentsMap = new Map<string, A2UIComponent>()
    for (const component of surface.components ?? []) {
        if (component.id) {
            componentsMap.set(component.id, component)
        }
    }
    return { surface, dataModel, componentsMap }
}

/**
 * Reducer for A2UI state management. Handles surface lifecycle events
 * (create/update/delete), local data model updates, and WebSocket envelope processing.
 */
function reducer(state: A2UIState, action: A2UIAction): A2UIState {
    switch (action.type) {
        case 'SURFACE_CREATED':
        case 'SURFACE_UPDATED': {
            const newSurfaces = new Map(state.surfaces)
            newSurfaces.set(action.surface.surfaceId, parseSurface(action.surface))
            return { ...state, surfaces: newSurfaces }
        }
        case 'SURFACE_DELETED': {
            const newSurfaces = new Map(state.surfaces)
            newSurfaces.delete(action.surfaceId)
            return { ...state, surfaces: newSurfaces }
        }
        case 'DATA_MODEL_UPDATED': {
            const surfaceState = state.surfaces.get(action.surfaceId)
            if (!surfaceState) return state
            const newDataModel = setByPointer(surfaceState.dataModel, action.path, action.value)
            const newSurfaces = new Map(state.surfaces)
            newSurfaces.set(action.surfaceId, { ...surfaceState, dataModel: newDataModel })
            return { ...state, surfaces: newSurfaces }
        }
        case 'SET_STATUS':
            return { ...state, status: action.status, error: action.error }
        case 'SET_CONVERSATION_ID':
            return { surfaces: new Map(), status: 'idle', error: undefined }
        case 'PROCESS_ENVELOPE': {
            const { message } = action.envelope
            if (message.oneofKind === 'surfaceCreated' && message.surfaceCreated.surface) {
                return reducer(state, { type: 'SURFACE_CREATED', surface: message.surfaceCreated.surface })
            }
            if (message.oneofKind === 'surfaceUpdated' && message.surfaceUpdated.surface) {
                return reducer(state, { type: 'SURFACE_UPDATED', surface: message.surfaceUpdated.surface })
            }
            if (message.oneofKind === 'surfaceDeleted') {
                return reducer(state, { type: 'SURFACE_DELETED', surfaceId: message.surfaceDeleted.surfaceId })
            }
            if (message.oneofKind === 'dataModelUpdated') {
                let newState = state
                for (const update of message.dataModelUpdated.updates) {
                    let value: unknown
                    try {
                        value = JSON.parse(update.value)
                    } catch {
                        value = update.value
                    }
                    newState = reducer(newState, {
                        type: 'DATA_MODEL_UPDATED',
                        surfaceId: message.dataModelUpdated.surfaceId,
                        path: update.path,
                        value,
                    })
                }
                return newState
            }
            return state
        }
        default:
            return state
    }
}

export interface UseA2UIOptions {
    conversationId?: string
    /** Called when a surface is created or updated */
    onSurfaceReceived?: (surface: A2UISurface) => void
}

export interface UseA2UIResult {
    surfaces: Map<string, SurfaceState>
    status: A2UIState['status']
    error?: string
    updateDataModel: (surfaceId: string, path: string, value: unknown) => void
}

/**
 * Hook for managing A2UI surfaces in a conversation. Subscribes to WebSocket updates,
 * fetches existing surfaces on mount, and provides methods to update local data models.
 */
export function useA2UI(options: UseA2UIOptions): UseA2UIResult {
    const { conversationId, onSurfaceReceived } = options
    const [state, dispatch] = useReducer(reducer, { surfaces: new Map(), status: 'idle' })
    const [a2uiService, setA2UIService] = useState<A2UIService | undefined>(() => getA2UIService())
    const a2uiClient = useA2UIClient()

    // Subscribe to service ready
    useEffect(() => onA2UIServiceReady(setA2UIService), [])

    // Stable ref for callback to avoid re-subscribing on every render
    const onSurfaceReceivedRef = useRef(onSurfaceReceived)
    useEffect(() => {
        onSurfaceReceivedRef.current = onSurfaceReceived
    }, [onSurfaceReceived])

    // Clear surfaces when conversation changes
    const currentConversationIdRef = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (currentConversationIdRef.current !== conversationId) {
            currentConversationIdRef.current = conversationId
            dispatch({ type: 'SET_CONVERSATION_ID', conversationId: conversationId ?? '' })
        }
    }, [conversationId])

    // Fetch existing surfaces on mount
    const fetchedRef = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (!conversationId) {
            // Reset fetch guard when conversation is cleared so future restores trigger refetch
            fetchedRef.current = undefined
            return
        }
        if (!a2uiClient || fetchedRef.current === conversationId) return
        fetchedRef.current = conversationId

        void (async (): Promise<void> => {
            try {
                const response = await listA2UISurfaces(a2uiClient, { conversationId })
                if (response.status === Status.OK && response.item) {
                    for (const surface of response.item.surfaces ?? []) {
                        dispatch({ type: 'SURFACE_UPDATED', surface })
                        onSurfaceReceivedRef.current?.(surface)
                    }
                }
            } catch {
                // Ignore fetch errors - surfaces will come via WebSocket
            }
        })()
    }, [conversationId, a2uiClient])

    // Subscribe to WebSocket updates
    useEffect(() => {
        if (!a2uiService || !conversationId) return

        const handleEnvelope: OnA2UISurfaceCallback = (envelope, envelopeConversationId): void => {
            // Defensive: only process envelopes for the current conversation
            if (envelopeConversationId !== conversationId) return
            dispatch({ type: 'PROCESS_ENVELOPE', envelope })

            // Notify when surfaces are created/updated
            const { message } = envelope
            if (message.oneofKind === 'surfaceCreated' && message.surfaceCreated.surface) {
                onSurfaceReceivedRef.current?.(message.surfaceCreated.surface)
            }
            if (message.oneofKind === 'surfaceUpdated' && message.surfaceUpdated.surface) {
                onSurfaceReceivedRef.current?.(message.surfaceUpdated.surface)
            }
        }

        a2uiService.subscribe(conversationId, handleEnvelope)
        dispatch({ type: 'SET_STATUS', status: 'connected' })

        return (): void => {
            a2uiService.unsubscribe(conversationId, handleEnvelope)
        }
    }, [a2uiService, conversationId])

    const updateDataModel = useCallback((surfaceId: string, path: string, value: unknown): void => {
        dispatch({ type: 'DATA_MODEL_UPDATED', surfaceId, path, value })
    }, [])

    return useMemo(() => ({
        surfaces: state.surfaces,
        status: state.status,
        error: state.error,
        updateDataModel,
    }), [state.surfaces, state.status, state.error, updateDataModel])
}
